<template>

    <v-card class="card-standard">
      <v-card-title tag="h2" class="typography-h5 weight-bold">
        {{ $t('analysis.improvements.title') }}
      </v-card-title>
      <v-card-text>
        <!-- The inline "before / after" teaser image used to sit here. It
             was Studio output rendered inside the 360 surface, which
             conflated the two products. Anything that *generates* a
             concept image now lives only in Studio. -->

        <div
          v-if="suggestions && suggestions.length > 0"
          class="improvement-card-grid"
          data-testid="improvement-card-grid"
        >
          <article
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="improvement-card"
            :class="[`improvement-card--${suggestion.priority}`, isPremiumSuggestion(index) ? 'improvement-card--locked' : null]"
            :data-testid="`improvement-card-${index}`"
            :data-priority="suggestion.priority"
            :data-category="suggestion.category"
          >
            <div class="improvement-icon-tile" :class="`tile-${suggestion.category}`" aria-hidden="true">
              <v-icon :icon="iconForCategory(suggestion.category)" size="36" />
            </div>
            <div class="improvement-body">
              <div class="improvement-meta">
                <v-chip
                  :color="getPriorityColor(suggestion.priority)"
                  size="x-small"
                  label
                  variant="tonal"
                >
                  {{ $t(`analysis.improvements.priority.${suggestion.priority}`) }}
                </v-chip>
                <span class="improvement-category">
                  {{ $t(`analysis.improvements.categories.${suggestion.category}`) }}
                </span>
              </div>

              <h3 class="improvement-title">
                <span v-if="isPremiumSuggestion(index)"
                      class="premium-blur"
                      @click="handlePremiumContent(suggestion.title)">
                  {{ suggestion.title.split(' ')[0] }}
                </span>
                <span v-if="isPremiumSuggestion(index)">
                  {{ suggestion.title.split(' ').slice(1).join(' ') }}
                </span>
                <span v-else>{{ suggestion.title }}</span>
              </h3>

              <PremiumContent
                :is-premium="isPremiumSuggestion(index)"
                @upgrade="handlePremiumContent(suggestion.title)"
              >
                <p
                  class="improvement-description"
                  v-html="formatMarkdownLite(suggestion.description)"
                ></p>
              </PremiumContent>

              <v-btn
                v-if="!isPremiumSuggestion(index)"
                class="improvement-cta"
                variant="tonal"
                color="primary"
                size="small"
                :to="studioRouteForSuggestion(suggestion, index)"
                :data-testid="`improvement-card-cta-${index}`"
              >
                <v-icon icon="mdi-auto-fix" start size="16" />
                {{ $t('analysis.improvements.openInStudio') }}
              </v-btn>
            </div>
          </article>
        </div>

        <div v-if="!suggestions || suggestions.length == 0" class="text-center pa-4 typography-body text-secondary">
          {{ $t('analysis.improvements.noSuggestions') }}
        </div>
      </v-card-text>
    </v-card>

</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import PremiumContent from '@/components/common/PremiumContent.vue'
import { formatMarkdownLite } from '@/utils/textFormatting'
import { trackSuggestionPremiumClick, trackUnlockCtaClicked } from '@/utils/analytics'
import { scrollToUnlockPanel } from '@/utils/scrollToUnlockPanel'
import { buildUnlockCtaPayload } from '@/utils/unlockTracking.mjs'

const store = useStore()
const route = useRoute()

// `originalImageUrl` + `teaserStatus` + `teaserImageSrc` were the legacy
// props for the inline Studio teaser image that used to render here. The
// inline image was removed (it conflated 360 + Studio); the props stay
// to avoid breaking the existing parent binding while we transition.
defineProps({
  originalImageUrl: { type: String, default: '' },
  teaserStatus: { type: String, default: 'idle' },
  teaserImageSrc: { type: String, default: '' },
});

// Kept exposed because Analysis.vue's IntersectionObserver still scrolls
// to this ref. Now points at the suggestion grid instead of the teaser.
const teaserSectionRef = ref(null);
defineExpose({ teaserSectionRef });

const hasPremiumAccess = computed(() => store.state.userHadPremiumWhenUploaded)

const isPremiumSuggestion = (index) => {
  if (hasPremiumAccess.value) return false
  return index > 0
}

const priorityOrder = { high: 3, medium: 2, low: 1 }

const sortedSuggestions = computed(() => {
  const suggestionsObject = store.state.analysis?.analysis?.suggestions;
  if (!suggestionsObject || typeof suggestionsObject !== 'object') {
    return [];
  }

  const transformedSuggestions = Object.entries(suggestionsObject).map(([key, value]) => ({
    key,
    title: value.title || key,
    description: value.description,
    priority: value.priority || 'low',
    category: value.category || 'general'
  }));

  return transformedSuggestions.sort((a, b) => {
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
});

const suggestions = sortedSuggestions

const getPriorityColor = (priority) => {
  const colors = {
    high: 'error',
    medium: 'warning',
    low: 'success'
  }
  return colors[priority] || 'info'
}

// Map each suggestion category to an MDI icon — mirrors the Studio
// starter-pack archetype tiles so the two surfaces feel related.
const CATEGORY_ICONS = {
  layout: 'mdi-floor-plan',
  rooms: 'mdi-door-open',
  flow: 'mdi-arrow-decision-outline',
  light: 'mdi-weather-sunny',
  storage: 'mdi-archive-outline',
  cost: 'mdi-currency-eur',
  accessibility: 'mdi-wheelchair-accessibility',
  privacy: 'mdi-shield-account-outline',
  outdoor: 'mdi-tree-outline',
  flexibility: 'mdi-vector-arrange-below',
  general: 'mdi-lightbulb-on-outline',
};
const iconForCategory = (category) => CATEGORY_ICONS[category] || CATEGORY_ICONS.general;

// Each suggestion card deep-links into the Studio with the suggestion
// text pre-filled into the command bar. Matches the existing Hub →
// Studio prompt link contract: `?prefill=<text>&source=<tag>`.
const studioRouteForSuggestion = (suggestion, index) => {
  const id = route?.params?.id || store.state.analysis?.analysisId || 'current';
  const description = (suggestion.description || '').trim();
  const prefill = description.length
    ? `${suggestion.title}. ${description}`
    : suggestion.title;
  return {
    name: 'AnalysisStudio',
    params: { id },
    query: {
      prefill,
      source: `improvement_suggestion_${suggestion.key || index}`,
    },
  };
};

// Add navigation helper
const navigateToPricing = () => {
  scrollToUnlockPanel()
}

// Add premium content handler
const handlePremiumContent = (suggestionTitle) => {
  trackSuggestionPremiumClick(suggestionTitle)
  trackUnlockCtaClicked(buildUnlockCtaPayload({
    source: 'improvement_suggestions',
    userIntent: store.state.userIntent,
    extra: {
      suggestionTitle,
    },
  }))

  if (!hasPremiumAccess.value) {
    navigateToPricing()
  }
}
</script>

<style scoped>
/* Card grid mirroring the Studio starter-pack so the two surfaces feel
 * like one product. Two columns on desktop, single column on mobile. */
.improvement-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
  margin-top: 8px;
  /* Without `start` alignment a tall card stretches its row neighbours
   * vertically (extra whitespace below short cards). */
  align-items: start;
}

.improvement-card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 14px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid rgba(36, 83, 70, 0.10);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(24, 39, 48, 0.04);
  transition: box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease;
}
.improvement-card:hover {
  box-shadow: 0 10px 24px rgba(24, 39, 48, 0.08);
  border-color: rgba(36, 83, 70, 0.18);
}
.improvement-card--locked {
  background: #fafaf6;
}
.improvement-card--high {
  border-left: 3px solid rgb(var(--v-theme-error));
}
.improvement-card--medium {
  border-left: 3px solid rgb(var(--v-theme-warning));
}
.improvement-card--low {
  border-left: 3px solid rgb(var(--v-theme-success));
}

.improvement-icon-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: #edf4f1;
  color: #245346;
}
.tile-cost { background: #fdf3e3; color: #6d3b1f; }
.tile-light { background: #fff7d4; color: #6d5916; }
.tile-storage { background: #ece8fa; color: #3e2f76; }
.tile-flow { background: #e1f1ff; color: #1f4f72; }
.tile-rooms { background: #ffe3ea; color: #6b1d34; }
.tile-accessibility { background: #e5f4eb; color: #1f5d40; }
.tile-privacy { background: #ece7df; color: #4b3d27; }
.tile-outdoor { background: #e0efe1; color: #1f4d2a; }

.improvement-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.improvement-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.improvement-category {
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.improvement-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f3a44;
  line-height: 1.3;
}

.improvement-description {
  margin: 0;
  color: #475a63;
  font-size: 0.9rem;
  line-height: 1.45;
  /* No clamp — the user explicitly asked for the full text. Cards
   * size to their content; the grid's `align-items: start` keeps
   * mismatched heights from stretching neighbours. */
  white-space: normal;
  overflow-wrap: anywhere;
}

.improvement-cta {
  align-self: flex-start;
  margin-top: 4px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}

:deep(.v-card-title) {
  font-size: inherit;
  font-weight: inherit;
}
:deep(.premium-content) {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .improvement-card {
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 12px;
    padding: 12px;
  }
  .improvement-icon-tile {
    width: 56px;
    height: 56px;
  }
}

/* Ensure the timeline dot aligns well on mobile */
:deep(.v-timeline-item__dot) {
  @media (max-width: 600px) {
    margin-inline-end: 8px;
  }
}

.premium-blur {
  filter: blur(4px);
  user-select: none;
  cursor: pointer;
  display: inline-block;
}

.premium-blur:hover {
  filter: blur(6px);
}

/* All .improvements-teaser-* styles were dropped along with the inline
 * Studio teaser image they used to style. The image is a Studio output;
 * 360 Verbesserungen should stay read-only. */
</style>
